/* HortaBerry Rendelek sections: lean by real order, one entrance per section,
   variant choice -> price / stock / form id / phone bar, photo thumbnails. Vanilla, no deps. */
(() => {
  // Every HortaBerry section includes this file, so it can arrive several times on one page.
  // Run it once: a second copy would add a second set of handlers (two add-to-cart requests
  // per click, "Tovább olvasom" toggling twice and looking dead).
  if (window.__hbRendelekLoaded) return;
  window.__hbRendelekLoaded = true;

  const mounted = new WeakMap();
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function afterViewTransition() {
    const vt = document.activeViewTransition;
    return vt && vt.finished ? vt.finished.catch(() => {}) : Promise.resolve();
  }

  function setLeans(root) {
    const amount = parseFloat(root.dataset.hbTilt) || 0;
    root.querySelectorAll('[data-hb-tilt-fixed]').forEach((el) => {
      el.style.setProperty('--hb-lean', (parseFloat(el.dataset.hbTiltFixed) || 0) + 'deg');
    });
    const groups = new Map();
    root.querySelectorAll('[data-hb-lean]').forEach((el) => {
      const g = el.closest('.hb-grid, .hbb-variants__grid') || root;
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g).push(el);
    });
    groups.forEach((list) => list.forEach((el, i) => {
      const sign = i % 2 ? 1 : -1;
      const k = 1 - (i % 3) * 0.25; // slight irregularity so it reads hand-placed
      el.style.setProperty('--hb-lean', (sign * amount * k).toFixed(2) + 'deg');
      el.toggleAttribute('data-hb-even', i % 2 === 1);
    }));
  }

  function entrance(root, cleanups) {
    if (root.dataset.hbMotion !== 'rise' || reduce() || !('IntersectionObserver' in window)) return;
    const cards = [...root.querySelectorAll('[data-hb-card]')];
    if (!cards.length) return;
    afterViewTransition().then(() => {
      if (!root.isConnected) return;
      const io = new IntersectionObserver((entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      }), { threshold: 0.12 });
      cards.forEach((el) => {
        // index among the cards of the same group (Shopify's block wrappers make parent-based counting wrong)
        const g = el.closest('.hb-grid') || root;
        const i = [...g.querySelectorAll('[data-hb-card]')].filter((c) => (c.closest('.hb-grid') || root) === g).indexOf(el);
        el.style.setProperty('--hb-delay', Math.max(0, i) * 90 + 'ms');
        io.observe(el);
      });
      root.setAttribute('data-hb-armed', '');
      const failsafe = setTimeout(() => cards.forEach((c) => c.classList.add('is-in')), 5000);
      cleanups.push(() => { io.disconnect(); clearTimeout(failsafe); });
    });
  }

  function buy(root, signal, cleanups) {
    const radios = [...root.querySelectorAll('.hbb-v__input')];
    const idInput = root.querySelector('[data-hbb-id]');
    const addBtn = root.querySelector('[data-hbb-add]');
    const bar = root.nextElementSibling && root.nextElementSibling.matches('[data-hbb-bar]') ? root.nextElementSibling : null;

    const update = () => {
      const r = radios.find((x) => x.checked) || radios.find((x) => !x.disabled);
      if (!r) return;
      r.checked = true;
      if (idInput && idInput.value !== r.value) {
        idInput.value = r.value;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const ok = r.dataset.available === 'true';
      if (addBtn) {
        addBtn.disabled = !ok;
        addBtn.dataset.available = String(ok);
        addBtn.textContent = ok ? addBtn.dataset.label : addBtn.dataset.sold;
      }
      root.querySelectorAll('[data-hbb-total]').forEach((el) => { el.textContent = r.dataset.price; });
      root.querySelectorAll('[data-hbb-compare]').forEach((el) => { el.textContent = r.dataset.compare || ''; });
      root.querySelectorAll('[data-hbb-per]').forEach((el) => { el.textContent = r.dataset.per || ''; });
      const n = r.dataset.stock === '' ? null : parseInt(r.dataset.stock, 10);
      root.querySelectorAll('[data-hbb-stock]').forEach((el) => {
        // ok (plenty) · low (≤ "Low stock from") · out (none left) – the colour follows data-level
        const level = (n !== null && n <= 0) || !ok ? 'out' : (n !== null && n <= (parseInt(el.dataset.threshold, 10) || 0)) ? 'low' : 'ok';
        el.dataset.level = level;
        const text = level === 'out' ? el.dataset.out : level === 'low' ? el.dataset.low : el.dataset.normal;
        el.textContent = (text || el.dataset.normal).replace('[count]', n === null ? '' : n);
      });
      if (bar) {
        const name = bar.querySelector('[data-hbb-bar-name]');
        const price = bar.querySelector('[data-hbb-bar-price]');
        if (name) name.textContent = r.dataset.name;
        if (price) price.textContent = r.dataset.price;
      }
    };
    // ---------- Photos: one function moves the slide, thumbnails, and (own-shape) frame ----------
    const slides = [...root.querySelectorAll('[data-hbb-slide]')];
    const photo = root.querySelector('.hbb__photo');
    const thumbs = [...root.querySelectorAll('[data-hbb-go]')];
    const showIndex = (i) => {
      if (i < 0 || i >= slides.length) return;
      slides.forEach((s, k) => s.classList.toggle('is-on', k === i));
      thumbs.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.hbbGo === String(i))));
      const r = parseFloat(slides[i].dataset.ratio);
      if (photo && r > 0) {
        if (photo.hasAttribute('data-hbb-auto-d')) photo.style.setProperty('--hbb-ratio', r);
        if (photo.hasAttribute('data-hbb-auto-m')) photo.style.setProperty('--hbb-ratio-m', r);
      }
    };
    const showMedia = (id) => showIndex(slides.findIndex((s) => s.dataset.mediaId === id));

    let stopAuto = () => {}; // replaced below when the slideshow is on

    // Swipe between photos on touch screens (vertical scrolling still works)
    if (photo && slides.length > 1) {
      let x0 = null; let y0 = 0;
      photo.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') { x0 = e.clientX; y0 = e.clientY; } }, { signal });
      photo.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0; const dy = e.clientY - y0; x0 = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        const cur = Math.max(0, slides.findIndex((s) => s.classList.contains('is-on')));
        showIndex((cur + (dx < 0 ? 1 : -1) + slides.length) % slides.length);
        stopAuto();
      }, { signal });
      photo.addEventListener('pointercancel', () => { x0 = null; }, { signal });
    }

    // Optional slideshow (Photo frame → Slideshow)
    const auto = root.querySelector('[data-hbb-autoplay]');
    if (auto && slides.length > 1 && !reduce()) {
      const ms = Math.max(2, parseFloat(auto.dataset.hbbAutoplay) || 5) * 1000;
      let timer = null; let stopped = false; let hovered = false; let inView = true;
      const current = () => Math.max(0, slides.findIndex((s) => s.classList.contains('is-on')));
      const schedule = () => {
        clearTimeout(timer);
        if (stopped || hovered || !inView || document.hidden) return;
        timer = setTimeout(() => { showIndex((current() + 1) % slides.length); schedule(); }, ms);
      };
      stopAuto = () => { if (auto.dataset.hbbStopOnPick !== 'false') { stopped = true; clearTimeout(timer); } else schedule(); };
      if (auto.dataset.hbbPauseHover !== 'false' && photo) {
        photo.addEventListener('pointerenter', () => { hovered = true; clearTimeout(timer); }, { signal });
        photo.addEventListener('pointerleave', () => { hovered = false; schedule(); }, { signal });
      }
      document.addEventListener('visibilitychange', schedule, { signal });
      if ('IntersectionObserver' in window && photo) {
        const io = new IntersectionObserver(([e]) => { inView = e.isIntersecting; schedule(); });
        io.observe(photo);
        cleanups.push(() => io.disconnect());
      }
      cleanups.push(() => clearTimeout(timer));
      schedule();
    }

    // Shopify's standard ?variant= link: pre-select it, and keep the address in step
    const wanted = new URLSearchParams(location.search).get('variant');
    const fromUrl = wanted && radios.find((x) => x.value === wanted && !x.disabled);
    if (fromUrl) fromUrl.checked = true;

    radios.forEach((r) => r.addEventListener('change', () => {
      update();
      if (r.dataset.media) { showMedia(r.dataset.media); stopAuto(); }
      if (!window.Shopify || !window.Shopify.designMode) {
        const url = new URL(location.href);
        url.searchParams.set('variant', r.value);
        history.replaceState(history.state, '', url);
      }
    }, { signal }));
    update();
    const first = radios.find((x) => x.checked);
    if (fromUrl && first && first.dataset.media) showMedia(first.dataset.media);

    // Product description "read more"
    root.querySelectorAll('[data-hbb-clamp]').forEach((box) => {
      const body = box.querySelector('.hbb-desc__body');
      const btn = box.querySelector('[data-hbb-more]');
      if (!body || !btn) return;
      const measure = () => {
        if (!box.offsetParent) return; // hidden (e.g. sold-out state): leave as is
        box.classList.add('is-clamped');
        const cut = body.scrollHeight > body.clientHeight + 2;
        box.classList.toggle('is-clamped', cut);
        btn.hidden = !cut;
      };
      measure();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
      btn.addEventListener('click', () => {
        const open = box.classList.toggle('is-clamped') === false;
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.dataset.less : btn.dataset.more;
      }, { signal });
    });

    // Photo thumbnails
    thumbs.forEach((btn) => btn.addEventListener('click', () => {
      showIndex(parseInt(btn.dataset.hbbGo, 10));
      stopAuto();
    }, { signal }));

    // Phone bar: shown once the main button has scrolled up out of view
    if (bar && addBtn) {
      let queued = false;
      const check = () => {
        queued = false;
        bar.classList.toggle('is-shown', addBtn.getBoundingClientRect().bottom < 0);
      };
      const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(check); } };
      window.addEventListener('scroll', onScroll, { passive: true, signal });
      check();
    }
  }

  // Add to cart without leaving the page (Buy buttons → After adding: Stay on this page)
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let refreshing = null;
  let refreshAgain = false;
  function refreshCartUi() {
    if (refreshing) { refreshAgain = true; return refreshing; }
    refreshing = doRefreshCartUi().finally(() => {
      refreshing = null;
      if (refreshAgain) { refreshAgain = false; refreshCartUi(); }
    });
    return refreshing;
  }

  async function doRefreshCartUi() {
    // Best effort, theme-version independent: re-render the sections that hold Savor's cart icon / drawer
    const els = ['cart-icon', 'cart-drawer-component'].map((s) => document.querySelector(s)).filter(Boolean);
    const ids = [...new Set(els.map((el) => {
      const sec = el.closest('[id^="shopify-section-"]');
      return sec ? sec.id.replace('shopify-section-', '') : null;
    }).filter(Boolean))];
    if (!ids.length) return;
    const res = await fetch(ROOT + '?sections=' + ids.map(encodeURIComponent).join(','));
    if (!res.ok) return;
    const data = await res.json();
    els.forEach((el) => {
      const sec = el.closest('[id^="shopify-section-"]');
      const html = sec && data[sec.id.replace('shopify-section-', '')];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(el.localName);
      if (fresh) el.replaceWith(fresh);
    });
  }

  function stayOnPage(root, signal) {
    const form = root.querySelector('form[data-hbb-after="stay"]');
    if (!form) return;
    const status = form.querySelector('[data-hbb-status]');
    const msg = form.querySelector('[data-hbb-status-msg]');
    const addBtn = form.querySelector('[data-hbb-add]');
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (addBtn) addBtn.disabled = true;
      root.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(ROOT + 'cart/add.js', {
          method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || '');
        const chosen = root.querySelector('.hbb-v__input:checked');
        const name = (chosen && chosen.dataset.name) || data.variant_title || '';
        msg.textContent = msg.dataset.ok.replace('[variant]', name);
        status.classList.remove('is-error');
        status.hidden = false;
        refreshCartUi().catch(() => {});
      } catch (err) {
        msg.textContent = err.message || 'Hiba történt, kérjük, próbáld újra.';
        status.classList.add('is-error');
        status.hidden = false;
      } finally {
        if (addBtn) addBtn.disabled = addBtn.dataset.available === 'false';
        root.removeAttribute('aria-busy');
      }
    }, { signal });
  }

  function mount(root) {
    if (mounted.has(root)) return;
    const ctl = new AbortController();
    const cleanups = [];
    setLeans(root);
    if (root.classList.contains('hbb')) { buy(root, ctl.signal, cleanups); stayOnPage(root, ctl.signal); }
    entrance(root, cleanups);
    mounted.set(root, () => { ctl.abort(); cleanups.forEach((f) => f()); });
  }

  function unmount(root) {
    const destroy = mounted.get(root);
    if (destroy) { destroy(); mounted.delete(root); }
  }

  const all = (scope) => scope.querySelectorAll('[data-hb-root]');
  const init = () => all(document).forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  document.addEventListener('shopify:section:load', (e) => all(e.target).forEach(mount));
  document.addEventListener('shopify:section:unload', (e) => all(e.target).forEach(unmount));
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target.closest('[data-hb-root]');
    if (root) root.querySelectorAll('[data-hb-card]').forEach((c) => c.classList.add('is-in'));
  });
})();
